import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { AlertTriangle, Building2, Clock, MailX, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { API } from "../lib/apiClient";
import { CompanyLogo, setCompaniesCache } from "../lib/companies";

const KIND = { renewal: "Yenileme hatırlatması", backup: "Haftalık yedek", monthly: "Aylık özet", quote: "Teklif", test: "Deneme", other: "Diğer" };
const fmt = (s) => (s ? new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "—");

export function CompaniesCard() {
  const [list, setList] = useState([]);
  const [logos, setLogos] = useState({});
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);
  const target = useRef("");
  const apply = (data, text) => {
    setList(data.companies); setLogos(data.companyLogos || {}); setCompaniesCache(data.companies, data.companyLogos || {}); setMsg(text);
  };
  useEffect(() => { axios.get(`${API}/user/preferences`).then(({ data }) => { setList(data.companies || []); setLogos(data.companyLogos || {}); }); }, []);
  const run = async (key, fn, text) => {
    setBusy(key); setMsg("");
    try { apply((await fn()).data, text); } catch (e) { setMsg(e?.response?.data?.detail || "Kaydedilemedi"); } finally { setBusy(""); }
  };
  const save = (next, text) => run("list", () => axios.put(`${API}/user/preferences`, { companies: next }), text);
  const add = (e) => {
    e.preventDefault();
    let name = draft.trim().replace(/\s+/g, " ");
    if (!name) return;
    if (!/sigorta/i.test(name)) name = `${name} Sigorta`;
    if (list.some((c) => c.toLocaleLowerCase("tr-TR") === name.toLocaleLowerCase("tr-TR"))) { setMsg("Bu şirket zaten listede"); return; }
    setDraft("");
    save([...list, name], `${name} eklendi`);
  };
  const remove = (c) => { if (window.confirm(`${c} listeden çıkarılsın mı? (Mevcut poliçeler etkilenmez)`)) save(list.filter((x) => x !== c), `${c} çıkarıldı`); };
  const pickLogo = (c) => { target.current = c; fileRef.current?.click(); };
  const uploadLogo = (file) => {
    const c = target.current;
    if (fileRef.current) fileRef.current.value = "";
    if (!file || !c) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) { setMsg("Sadece PNG, JPG veya WebP"); return; }
    if (file.size > 1024 * 1024) { setMsg("Logo en fazla 1MB olabilir"); return; }
    const fd = new FormData();
    fd.append("company", c); fd.append("file", file, file.name);
    run(c, () => axios.post(`${API}/user/company-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }), `${c} logosu yüklendi`);
  };
  const removeLogo = (c) => run(c, () => axios.delete(`${API}/user/company-logo`, { params: { company: c } }), `${c} logosu kaldırıldı`);
  return (
    <section className="panel settings-card" data-testid="companies-card">
      <div className="settings-card-head"><Building2 size={16} /> <h2>Sigorta şirketleri</h2><span className="muted-count">{list.length}</span></div>
      <p className="privacy-note">Poliçe ve teklif eklerken şirket bu listeden seçilir. Sonuna "Sigorta" yazmazsanız otomatik eklenir. Yaygın şirketlerin logosu otomatik gelir; dilediğiniz şirkete kendi logonuzu yükleyebilirsiniz.</p>
      <form className="company-add" onSubmit={add}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Örn: Hepiyi" maxLength={60} data-testid="company-add-input" />
        <button className="primary-button" disabled={!!busy || !draft.trim()} data-testid="company-add-button"><Plus size={14} /> Ekle</button>
      </form>
      {msg && <small className="signature-hint" data-testid="companies-msg">{msg}</small>}
      <input type="file" ref={fileRef} accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="company-logo-file-input" />
      <div className="company-rows" data-testid="company-list">
        {list.map((c) => (
          <div key={c} className="company-row" data-testid={`company-chip-${c}`}>
            <CompanyLogo name={c} size={28} testId={`company-logo-${c}`} />
            <span className="company-row-name">{c}{logos[c] && <em className="company-own-tag">özel logo</em>}</span>
            <div className="company-row-actions">
              <button type="button" className="text-button" onClick={() => pickLogo(c)} disabled={!!busy} data-testid={`company-logo-upload-${c}`}>
                <Upload size={13} /> {busy === c ? "Yükleniyor…" : logos[c] ? "Değiştir" : "Logo yükle"}
              </button>
              {logos[c] && <button type="button" className="text-button danger" onClick={() => removeLogo(c)} disabled={!!busy} data-testid={`company-logo-remove-${c}`}><Trash2 size={13} /> Logoyu sil</button>}
              <button type="button" className="row-action" onClick={() => remove(c)} disabled={!!busy} title="Listeden çıkar" data-testid={`company-remove-${c}`}><X size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function EmailQueueCard() {
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/email/queue`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const cancel = async (it) => {
    if (!window.confirm(`"${it.subject}" gönderimi iptal edilsin mi?`)) return;
    try { await axios.delete(`${API}/email/queue/${it.id}`); } catch (e) { window.alert(e?.response?.data?.detail || "İptal edilemedi"); }
    load();
  };
  return (
    <section className="panel settings-card" data-testid="email-queue-card">
      <div className="settings-card-head"><Clock size={16} /> <h2>Gönderim kuyruğu</h2><span className="muted-count">{items.length}</span></div>
      <p className="privacy-note">E-posta servisinin hız sınırına takılan e-postalar burada bekler ve birkaç dakikada bir otomatik gönderilir.</p>
      {items.length === 0 ? <p className="queue-empty" data-testid="email-queue-empty">Kuyrukta bekleyen e-posta yok, tüm gönderimler iletildi.</p> : (
        <ul className="queue-list">
          {items.map((it) => (
            <li key={it.id} data-testid={`email-queue-item-${it.id}`}>
              <div><strong>{it.subject}</strong><span>{KIND[it.kind] || it.kind} → {it.to} · Sıraya alındı {fmt(it.createdAt)}{it.attempts ? ` · ${it.attempts} deneme` : ""}</span></div>
              <em data-testid={`email-queue-eta-${it.id}`}>Tahmini: {new Date(it.eta).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</em>
              <button className="icon-button" onClick={() => cancel(it)} title="İptal et" data-testid={`email-queue-cancel-${it.id}`}><Trash2 size={15} /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function useUndeliverable() {
  const [rows, setRows] = useState([]);
  useEffect(() => { axios.get(`${API}/email/undeliverable`).then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return rows;
}

export function UndeliverableCard({ onEditCustomer }) {
  const rows = useUndeliverable();
  return (
    <section className="panel settings-card" data-testid="undeliverable-card">
      <div className="settings-card-head"><MailX size={16} /> <h2>Teslim edilemeyen e-posta adresleri</h2><span className={`muted-count ${rows.length ? "danger" : ""}`}>{rows.length}</span></div>
      {rows.length === 0 ? <p className="queue-empty" data-testid="undeliverable-empty">Tüm müşteri e-posta adresleri sorunsuz görünüyor.</p> : (
        <>
          <p className="undeliverable-warn"><AlertTriangle size={14} /> Bu müşterilere e-posta ulaşmıyor. Doğru adresi girin; yeni adrese gönderim otomatik başlar.</p>
          <ul className="queue-list">
            {rows.map((r) => (
              <li key={`${r.customerId}-${r.email}`} data-testid={`undeliverable-${r.customerId}`}>
                <div><strong>{r.name}</strong><span>{r.email} · {r.reason}{r.at ? ` · ${fmt(r.at)}` : ""}</span></div>
                <button className="outline-button" onClick={() => onEditCustomer?.(r.customerId)} data-testid={`undeliverable-edit-${r.customerId}`}><Pencil size={13} /> Adresi düzelt</button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
